import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, FileText, AlertTriangle } from 'lucide-react';
import { SigeEinweisung } from '../sige-plan/types';
import {
  createEinweisung,
  updateEinweisung,
  uploadEinweisungsDokument,
} from '../../services/sigeEinweisungService';
import { describeError } from '../../services/errorMessage';
import { PAPIER_DATEI_TYPEN, heuteIso, pruefePapierDatei } from './einweisungLogic';

interface Props {
  projectId: string;
  /** Vorbelegung des Durchfuehrenden (angemeldeter Benutzer). */
  vorbelegterName: string;
  isReadOnly?: boolean;
  isMobile?: boolean;
  onClose: () => void;
  /**
   * Der fertige Datensatz. Der Container nimmt ihn in die Ablage auf UND
   * stellt unmittelbar danach die Abrechnungsfrage — derselbe Weg wie beim
   * digitalen Abschliessen.
   */
  onFertig: (einweisung: SigeEinweisung) => void;
}

/**
 * Eine auf Papier gehaltene Einweisung nachtragen.
 *
 * Auf der Baustelle wird oft mit Klemmbrett eingewiesen — der unterschriebene
 * Zettel ist dann das Original. Der Datensatz hier ist trotzdem vollwertig: er
 * wird gezaehlt, abgerechnet und ausgewertet wie jeder andere (siehe
 * EinweisungQuelle in sige-plan/types.ts). Deshalb steht auch hier die
 * Abrechnungsfrage am Ende und nicht irgendwo anders.
 *
 * ─── Reihenfolge ──────────────────────────────────────────────────────────
 * 1. Datensatz anlegen — erst dann gibt es die ID, unter der die Datei im
 *    Bucket abgelegt wird (`papier/<einweisungId>/<uuid>.<endung>`).
 * 2. Datei hochladen.
 * 3. Verweis am Datensatz eintragen.
 * Bricht Schritt 2 oder 3 ab, bleibt ein Entwurf ohne Dokument stehen: er ist
 * sichtbar, benannt und laesst sich erneut befuellen. Ein stilles Aufraeumen
 * waere gefaehrlicher — es koennte einen Datensatz loeschen, dessen Upload in
 * Wahrheit durchlief und dessen Antwort nur nicht ankam.
 *
 * Der Status bleibt bewusst ENTWURF: Datum und Durchfuehrender einer
 * nachgetragenen Einweisung werden erfahrungsgemaess noch einmal korrigiert.
 * Abgeschlossen wird sie wie jede andere im Editor.
 */
export const PapierUploadDialog: React.FC<Props> = ({
  projectId,
  vorbelegterName,
  isReadOnly = false,
  isMobile = false,
  onClose,
  onFertig,
}) => {
  const dateiRef = useRef<HTMLInputElement>(null);
  /**
   * Der im ERSTEN Anlauf angelegte Datensatz.
   *
   * Bricht der Upload danach ab (auf der Baustelle der Normalfall: das Netz
   * traegt den Scan nicht), steht die Zeile bereits in der Tabelle. Ein
   * zweiter Klick auf „Hochladen" legte sonst Nr. n+1 an — und im Modul gibt
   * es kein Loeschen, die halbe Zeile bliebe fuer immer stehen. Der zweite
   * Anlauf befuellt deshalb denselben Datensatz.
   */
  const angelegtRef = useRef<SigeEinweisung | null>(null);
  /** Riegel gegen den zweiten Fingertipp — wirkt sofort, anders als `laeuft`. */
  const laeuftRef = useRef(false);
  const [datei, setDatei] = useState<File | null>(null);
  const [datum, setDatum] = useState(heuteIso());
  const [ort, setOrt] = useState('');
  const [durchfuehrender, setDurchfuehrender] = useState(vorbelegterName);
  const [bemerkung, setBemerkung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const feld = `w-full px-3 py-2 ${isMobile ? 'min-h-[44px]' : ''} text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 disabled:text-slate-500`;

  const waehlen = (f: File | null) => {
    setDatei(f);
    const pruefung = pruefePapierDatei(f);
    setFehler(f && !pruefung.ok ? pruefung.fehler || null : null);
  };

  const hochladen = async () => {
    // Nur-Ansicht: der Riegel sitzt hier UND am Knopf. Ein Upload legt eine
    // Datei im Bucket und eine Zeile in der Tabelle an — beides ueberlebt das
    // Schliessen des Projekts und waere in einem fremden Projekt dauerhaft.
    if (isReadOnly) {
      setFehler('Nur-Ansicht: In diesem Projekt lässt sich nichts hochladen.');
      return;
    }
    const pruefung = pruefePapierDatei(datei);
    if (!pruefung.ok) {
      setFehler(pruefung.fehler || 'Bitte eine gültige Datei auswählen.');
      return;
    }
    if (laeuftRef.current) return;

    laeuftRef.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      // Beim Wiederholen wird der Datensatz des ersten Anlaufs weiterbenutzt,
      // statt eine zweite Nummer zu ziehen.
      const angelegt = angelegtRef.current ?? await createEinweisung({
        project_id: projectId,
        quelle: 'papier',
        datum,
        ort: ort.trim() || null,
        durchfuehrender_name: durchfuehrender.trim() || null,
        bemerkung: bemerkung.trim() || null,
        // Auf Papier gibt es keine erfassten Punkte und keine digitalen
        // Unterschriften — das Blatt selbst ist der Beleg.
        punkte: [],
        teilnehmer: [],
        leerzeilen: 0,
      });
      angelegtRef.current = angelegt;

      const { path, fileName } = await uploadEinweisungsDokument(angelegt.id, datei!, datei!.name);
      // Die Kopfdaten gehen hier mit: zwischen den beiden Anlaeufen kann
      // korrigiert worden sein, und der Datensatz von oben traegt dann noch
      // den alten Stand.
      const fertig = await updateEinweisung(angelegt.id, {
        quelle: 'papier',
        datum,
        ort: ort.trim() || null,
        durchfuehrender_name: durchfuehrender.trim() || null,
        bemerkung: bemerkung.trim() || null,
        dokument_pfad: path,
        dokument_name: fileName,
      });

      angelegtRef.current = null;
      onFertig(fertig);
    } catch (e) {
      setFehler(
        `${describeError(e)}${angelegtRef.current
          ? ' Die Einweisung ist bereits angelegt — „Hochladen" wiederholt nur den Scan, es entsteht keine zweite.'
          : ''}`,
      );
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h2 className="text-sm font-bold text-slate-800">Einweisung in Papierform hinterlegen</h2>
          <button onClick={onClose} disabled={laeuft} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full disabled:opacity-40">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-xs text-slate-500">
            Scan, Foto oder PDF der unterschriebenen Liste. Der Datensatz zählt und rechnet ab
            wie eine digital geführte Einweisung.
          </p>

          <button
            type="button"
            onClick={() => dateiRef.current?.click()}
            disabled={laeuft || isReadOnly}
            className="w-full min-h-[88px] px-4 py-4 flex flex-col items-center justify-center gap-2 text-sm font-semibold text-slate-600 bg-slate-50 border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/40 rounded-xl transition-colors disabled:opacity-40"
          >
            {datei ? <FileText className="w-6 h-6 text-blue-600" /> : <Upload className="w-6 h-6 text-slate-400" />}
            <span className="truncate max-w-full">{datei ? datei.name : 'Datei wählen (PDF oder Bild)'}</span>
          </button>
          <input
            ref={dateiRef}
            type="file"
            accept={PAPIER_DATEI_TYPEN}
            className="hidden"
            onChange={(e) => waehlen(e.target.files?.[0] || null)}
          />

          <label className="block">
            <span className="block text-xs font-semibold text-slate-500 mb-1">Datum der Einweisung</span>
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} disabled={laeuft} className={feld} />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-500 mb-1">Ort</span>
            <input value={ort} onChange={(e) => setOrt(e.target.value)} disabled={laeuft} placeholder="z. B. Baustellenbüro" className={feld} />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-500 mb-1">Durchführender</span>
            <input value={durchfuehrender} onChange={(e) => setDurchfuehrender(e.target.value)} disabled={laeuft} className={feld} />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-500 mb-1">Bemerkung</span>
            <textarea value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} disabled={laeuft} rows={2} className={feld} />
          </label>

          {fehler && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" /> <span>{fehler}</span>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={laeuft} className="px-4 py-2.5 min-h-[44px] text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-40">
            Abbrechen
          </button>
          <button
            onClick={() => void hochladen()}
            disabled={laeuft || isReadOnly || !datei}
            className="px-5 py-2.5 min-h-[44px] flex items-center gap-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-40"
          >
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {laeuft ? 'Lädt hoch…' : 'Hochladen'}
          </button>
        </div>
      </div>
    </div>
  );
};
